import React, { useCallback, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import * as statusCodes from "./statusCodes.jsx";
import { fetch } from "../../utils/fetch.jsx";

export const FilePreviewStatusChecker = ({ url, check, onCheck, children }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof onCheck === "function") {
      onCheck({
        status: statusCodes.PREVIEW_UNKNOWN,
        progress: 0,
      });
    }
  }, [url, onCheck]);

  useEffect(() => {
    const updateTimeout = ref.current;
    return () => {
      clearTimeout(updateTimeout);
    };
  }, []);

  const updatePreview = useCallback(
    (previewUrl) => {
      clearTimeout(ref.current);

      let nextStatus = statusCodes.PREVIEW_UNKNOWN;

      fetch({
        url: previewUrl,
        method: "head",
        cors: true,
        detailed: true,
      })
        .then((response) => {
          if (response.status === 200) {
            nextStatus = statusCodes.PREVIEW_READY;
          } else {
            nextStatus = statusCodes.PREVIEW_IN_PROGRESS;
          }

          if (response.status === 202) {
            ref.current = setTimeout(() => {
              updatePreview(previewUrl);
            }, 3000);
          }

          if (typeof onCheck === "function") {
            onCheck({
              status: nextStatus,
              progress: parseInt(response.headers.get("X-Progress-Percentage")),
            });
          }
        })
        .catch((exception) => {
          if (exception?.response && [422, 500, 404, 400].indexOf(exception?.status_code) > -1) {
            if (exception?.response?.status === 422 || exception?.status_code === 422) {
              nextStatus = statusCodes.PREVIEW_NOT_AVAILABLE;
            } else if (exception?.response?.status === 404 || exception?.status_code === 404) {
              nextStatus = statusCodes.PREVIEW_ORIGINAL_MISSING;
            } else if (exception?.response?.status === 400 || exception?.status_code === 400) {
              nextStatus = statusCodes.PREVIEW_LOCKED;
            } else {
              nextStatus = statusCodes.PREVIEW_FAILED;
            }

            if (typeof onCheck === "function") {
              onCheck({
                status: nextStatus,
                progress: 0,
              });
            }
          } else {
            console.error(exception);
          }
        });
    },
    [onCheck]
  );

  useEffect(() => {
    if (check) {
      updatePreview(url);
    }
  }, [updatePreview, check, url]);

  return <>{children}</>;
};

FilePreviewStatusChecker.propTypes = {
  url: PropTypes.string.isRequired,
  check: PropTypes.bool,
  onCheck: PropTypes.func,
  children: PropTypes.node,
};

FilePreviewStatusChecker.defaultProps = {
  check: false,
};
